import CategoryManagerState from "@/modules/category-manager";
import TitleListState from "@/modules/title-list";
import { VersionGroupModule } from "@/modules/versiongroup";
import { VersionGroupModel, VersionGroupService } from "@/services";
import {
	CompareContentChapterModel,
	CompareContentModel,
	CompareContentTextModel,
	ContentService,
	RelatedContentResponseModel
} from "@/services/content";
import { Select, Spin, Alert } from "ant-design-vue";
import { fmtMsg, isGuid, MessageHelper, NotificationType, CurriculumType } from "commonui";
import * as Diff from "diff";
import * as Diff2Html from "diff2html";
import { Component, Prop, Vue } from "vue-property-decorator";
import { getModule } from "vuex-module-decorators";
import "diff2html/bundles/css/diff2html.min.css";
import { TitleListLocale } from "@/locales/localeid";
import { max, range } from "lodash";
import { NonCurriculumComponentType } from "@/util/enum";

@Component({
	components: {
		Select,
		SelectOption: Select.Option,
		Spin,
		Alert
	}
})
export default class VersionComparer extends Vue {
	@Prop() hideComparer!: Function;
	titleListModule = getModule(TitleListState);
	categoryModule = getModule(CategoryManagerState);
	locale = TitleListLocale;
	fmtMsg = fmtMsg;
	fields = [
		fmtMsg(TitleListLocale.TitleListCompareCategory),
		fmtMsg(TitleListLocale.TitleListCompareSubCategory),
	];
	loading = true;
	allRelatedContent: RelatedContentResponseModel[] = [];
	contents: CompareContentModel[] = [];
	leftVersionId: string = "";
	rightVersionId: string = "";
	leftRevisionSelected: number = 0;
	rightRevisionSelected: number = 0;
	leftVersions: VersionGroupModel[] = [];
	rightVersions: VersionGroupModel[] = [];
	leftSelectedChapter = "none";
	rightSelectedChapter = "none";
	leftChapters: CompareContentChapterModel[] = [];
	rightChapters: CompareContentChapterModel[] = [];
	isUnitBased = this.titleListModule.isunitBased === NonCurriculumComponentType.Unit;

	mounted() {
		if(this.isUnitBased) {
			this.fields.push(fmtMsg(TitleListLocale.TitleListCompareUnit))
		}
	}

	created() {
		// fetch content related to editedcontent
		ContentService.getRelatedContent(this.titleListModule.editedContentId)
			.then((content) => {
				this.allRelatedContent = content;
				this.loading = true;
				// fetch first two
				if (content.length) {
					const {
						editedContentId,
						editedContentDetails,
						currentRevision,
						editeContentSelectedChapterId
					} = this.titleListModule;

					this.leftVersionId =
						editedContentDetails.curriculumType === CurriculumType.GrapeSEED
							? VersionGroupModule.selectedGsVersion
							: VersionGroupModule.selectedLsVersion;
					this.rightVersionId = content.find((c) => c.id !== editedContentId)?.versionGroupId as string;
					this.rightVersionId = this.rightVersionId ? this.rightVersionId : this.leftVersionId; // if no version is available then, make left right equal.
					const contentIds = [editedContentId];

					if (this.rightVersionId !== this.leftVersionId) {
						// find content of version
						contentIds.push(content.find((c) => c.versionGroupId === this.rightVersionId)?.id as string);
					}

					ContentService.getContent(contentIds as string[])
						.then((contents) => {
							this.contents = contents;

							const lContent = this.leftContent();
							// Handle left chapter and revision
							this.leftRevisionSelected = currentRevision;
							if (editeContentSelectedChapterId !== "none") {
								this.leftSelectedChapter = editeContentSelectedChapterId;
							} else {
								this.leftSelectedChapter = lContent.contentChapter.length
									? lContent.contentChapter[0].id
									: "none";
							}

							// handle right chapter and revision
							this.rightSelectedChapter = this.rightContent().contentChapter.length
								? this.rightContent().contentChapter[0].id
								: "none";
							this.updateRevision(); // update right revision

							// update left and right version lists
							this.updateLeftVersions();
							this.updateRightVersions();
							this.loading = false;
						})
						.catch((e) => {
							MessageHelper.Message(
								NotificationType.Failed,
								fmtMsg(TitleListLocale.TitleListCompareLoadFail)
							);
						});
				} else {
					this.loading = false;
				}
			})
			.catch((e) => {
				MessageHelper.Message(NotificationType.Failed, fmtMsg(TitleListLocale.TitleListCompareLoadFail));
			});
	}

	get leftRevisions() {
		const leftContent = this.leftContent();
		const maxRevisions = this.getMaxRevisionForContent(leftContent, this.leftSelectedChapter);
		if (leftContent && maxRevisions) {
			return range(0, maxRevisions + 1);
		}
		return [0];
	}

	get rightRevisions() {
		const rightContent = this.rightContent();
		const maxRevisions = this.getMaxRevisionForContent(rightContent, this.leftSelectedChapter);
		if (rightContent && maxRevisions) {
			return range(0, maxRevisions + 1);
		}
		return [0];
	}

	get versionName() {
		if (this.leftContent()) {
			return this.leftContent().title;
		}
		return "";
	}

	get doesLeftHaveChapter() {
		const content = this.leftContent();
		if (content) {
			return content?.contentChapter.length;
		}
		return false;
	}

	get doesRightHaveChapter() {
		const content = this.rightContent();
		if (content) {
			return content?.contentChapter.length;
		}
		return false;
	}

	getMaxRevisionForContent(content: CompareContentModel, selectedChapterId: string) {
		let revisions = selectedChapterId && content?.contentChapter.length
			? content?.contentChapter
				.find((c) => c.id === selectedChapterId)
				?.contentTextGroupByRevision.map((c) => c.revision)
			: content.revContentText.map((f) => f.revision);
		revisions = revisions && revisions.length ? revisions : [0];
		return max(revisions);
	}

	updateLeftVersions() {
		const versions = this.allRelatedContent.map((v) => v.versionGroupId);
		this.leftVersions = VersionGroupModule.versionGroups.filter((v) => versions.some((vs) => vs === v.id));
	}

	updateRightVersions() {
		const versions = this.allRelatedContent.map((v) => v.versionGroupId);
		this.rightVersions = VersionGroupModule.versionGroups.filter((v) => versions.some((vs) => vs === v.id));
	}

	leftContent(): CompareContentModel {
		const content = this.allRelatedContent.find((c) => c.versionGroupId === this.leftVersionId);
		return this.contents.find((c) => c.id === content?.id) as CompareContentModel;
	}

	rightContent(): CompareContentModel {
		const content = this.allRelatedContent.find((c) => c.versionGroupId === this.rightVersionId);
		return this.contents.find((c) => c.id === content?.id) as CompareContentModel;
	}

	leftContentText() {
		// returns left contentText (From chapter or content)
		const content = this.leftContent();
		if (content) {
			return this.leftSelectedChapter && content?.contentChapter.length
				? content.contentChapter
					.find((c) => c.id === this.leftSelectedChapter)
					?.contentTextGroupByRevision.find((c) => c.revision === this.leftRevisionSelected)?.contentText
				: content.revContentText.find((f) => f.revision === this.leftRevisionSelected)?.contentText;
		}
		return [];
	}

	rightContentText() {
		// returns right contentText (From chapter or content)
		const content = this.rightContent();
		if (content) {
			return this.rightSelectedChapter && content.contentChapter.length
				? content.contentChapter
					.find((c) => c.id === this.rightSelectedChapter)
					?.contentTextGroupByRevision.find((c) => c.revision === this.rightRevisionSelected)?.contentText
				: content.revContentText.find((f) => f.revision === this.rightRevisionSelected)?.contentText;
		}
		return [];
	}

	getValue(index: number, isLeft: boolean) {
		const content = isLeft ? this.leftContent() : this.rightContent();
		if (!content) return "";
		if(this.isUnitBased) {
			switch (index) {
				case 0: {
					const category = this.categoryModule.categories.find(
						(c) => c.types.findIndex((t) => t.id === content.contentTypeId) > -1
					);
					return category?.name;
				}
				case 1: {
					const category = this.categoryModule.categories.find(
						(c) => c.types.findIndex((t) => t.id === content.contentTypeId) > -1
					);
					return category?.types.find((t) => t.id === content.contentTypeId)?.name;
				}
				case 2: {
					return this.titleListModule.levels.find((l) => l.id === content.vcsLevelId)?.displayName;
				}
				default:
					return "";
			}
		}
		else {
			switch (index) {
				case 0: {
					const category = this.categoryModule.categories.find(
						(c) => c.types.findIndex((t) => t.id === content.contentTypeId) > -1
					);
					return category?.name;
				}
				case 1: {
					const category = this.categoryModule.categories.find(
						(c) => c.types.findIndex((t) => t.id === content.contentTypeId) > -1
					);
					return category?.types.find((t) => t.id === content.contentTypeId)?.name;
				}
				default:
					return "";
			}
		}
	}

	async onNameClick() {
		const contentLicenseType = VersionGroupModule.versionGroups.find((v) => v.id === this.leftVersionId)
			?.curriculumType;

		const refreshContent = async (updateVersion = false, force = false) => {
			// update version
			const gsVersion =
				contentLicenseType === CurriculumType.GrapeSEED
					? this.leftVersionId
					: isGuid(VersionGroupModule.selectedGsVersion)
					? VersionGroupModule.selectedGsVersion
					: null;

			const lsVersion =
				contentLicenseType === CurriculumType.LittleSEED
					? this.leftVersionId
					: isGuid(VersionGroupModule.selectedLsVersion)
					? VersionGroupModule.selectedLsVersion
					: null;
			if (updateVersion) {
				contentLicenseType === CurriculumType.GrapeSEED &&
					VersionGroupModule.setGsVersion(gsVersion ? gsVersion : "");
				contentLicenseType === CurriculumType.LittleSEED &&
					VersionGroupModule.setLsVersion(lsVersion ? lsVersion : "");
				await VersionGroupService().saveVersionGroupSelected({
					selectedGSVersion: gsVersion as string,
					selectedLSVersion: lsVersion as string
				});
			}
			this.hideComparer(this.leftContent().id, force);
			this.loading = true;
		};
		// if content version or revision is different than user selected... update user version
		if (
			contentLicenseType === CurriculumType.GrapeSEED
				? this.leftVersionId !== VersionGroupModule.selectedGsVersion
				: this.leftVersionId !== VersionGroupModule.selectedLsVersion
		) {
			await refreshContent(true);
		} else if (this.titleListModule.revisionChanged) {
			this.titleListModule.setRevisionChanged(false);
			await refreshContent(false, true);
		} else {
			this.hideComparer();
		}
	}

	onVersionChange(versionId: string, isLeft: boolean = false) {
		const contentId = this.allRelatedContent.find((c) => c.versionGroupId === versionId)?.id;

		if (isLeft) {
			// update left version info
			this.leftVersionId = versionId;
		} else {
			// update right version
			this.rightVersionId = versionId;
		}
		if (!this.contents.find((c) => c.id === contentId)) {
			this.loading = true;
			ContentService.getContent([contentId] as string[])
				.then((contents) => {
					this.contents = [...this.contents, ...contents];
					this.updateLeftVersions();
					this.updateRightVersions();
					this.resetChapterAndRevision(isLeft);
					this.loading = false;
				})
				.catch((e) => {
					MessageHelper.Message(NotificationType.Failed, fmtMsg(TitleListLocale.TitleListCompareLoadFail));
				});
		} else {
			this.updateLeftVersions();
			this.updateRightVersions();
			this.resetChapterAndRevision(isLeft);
		}
	}

	resetChapterAndRevision(isLeft = false) {
		if (isLeft) {
			const content = this.leftContent();
			this.leftSelectedChapter = content.contentChapter.length ? content.contentChapter[0].id : "none";
			this.titleListModule.setEditeContentSelectedChapterId(this.leftSelectedChapter);
			this.updateRevision(true); // left
		} else {
			const content = this.rightContent();
			this.rightSelectedChapter = content.contentChapter.length ? content.contentChapter[0].id : "none";
			this.updateRevision(); // right
		}
	}

	onRevisionChange(revision: number, isLeft: boolean) {
		if (isLeft) {
			this.leftRevisionSelected = revision;
			this.titleListModule.setRevision(revision);
			this.titleListModule.setRevisionChanged(true);
		} else {
			this.rightRevisionSelected = revision;
		}
	}

	updateRevision(isLeft: boolean = false) {
		if (isLeft) {
			const leftContent = this.leftContent();
			if (leftContent.contentChapter.length && this.leftSelectedChapter !== "none") {
				let chapRev = leftContent.contentChapter
					.find((c) => c.id === this.leftSelectedChapter)
					?.contentTextGroupByRevision.map((ct) => ct.revision);
				chapRev = chapRev && chapRev.length ? chapRev : [0];
				this.leftRevisionSelected = max(chapRev) as number;
			} else {
				this.leftRevisionSelected = max(leftContent.revContentText.map((r) => r.revision))!;
			}
			this.titleListModule.setRevision(this.leftRevisionSelected);
		} else {
			const rightContent = this.rightContent();
			if (rightContent.contentChapter.length && this.rightSelectedChapter) {
				let chapRev = rightContent.contentChapter
					.find((c) => c.id === this.rightSelectedChapter)
					?.contentTextGroupByRevision.map((ct) => ct.revision);
				chapRev = chapRev && chapRev.length ? chapRev : [0];
				this.rightRevisionSelected = max(chapRev) as number;
			} else {
				this.rightRevisionSelected = max(rightContent.revContentText.map((r) => r.revision))!;
			}
		}
	}

	compare(): string {
		const leftContent = this.leftContent();
		const rightContent = this.rightContent();
		if (!leftContent && !rightContent) {
			return `<div>${fmtMsg(TitleListLocale.TitleListCompareComparing)}</div>`;
		}

		// choose left and right contentText (From chapter or content)
		let leftText: any | CompareContentTextModel[] = this.leftContentText();

		let rightText: any | CompareContentTextModel[] = this.rightContentText();

		// if text is undefined, set empty array
		leftText = leftText ? leftText : [];
		rightText = rightText ? rightText : [];

		let diff: string = Diff.createPatch(
			"file",
			leftText.map((c) => c.textContent).join("\n"),
			rightText.length ? rightText.map((c) => c.textContent).join("\n") : ""
		);

		// the diff will not return anything if there is no difference
		// so create a custom output
		if (diff.trim().endsWith("+++ file")) {
			diff = "";
			leftText.forEach((c) => {
				diff += `<tr><td class="d2h-cntx">
				<div class="d2h-code-side-line d2h-cntx">
					<span class="d2h-code-line-ctn">   ${c.textContent}</span>
				</div>
			</td></tr>`;
			});
			diff += `</tbody></table>`;
			const template = this.compareTemplate().replace(
				"{{{diffs.right}}}",
				diff.replace(/d2h-code-line-ctn/g, "d2h-code-line-ctn vc__right")
			);
			return `<div class="d2h-wrapper">${template.replace("{{{diffs.left}}}", diff)}</div>`;
		}
		return Diff2Html.html(diff, {
			matching: "lines",
			outputFormat: "side-by-side",
			drawFileList: false,
			rawTemplates: {
				"side-by-side-file-diff": this.compareTemplate()
			}
		});
	}

	compareTemplate() {
		return `<div id="{{fileHtmlId}}" class="d2h-file-wrapper sbs-wrap" data-lang="{{file.language}}">
		<div class="d2h-files-diff">
			<div class="d2h-file-side-diff sbs-wrap__left">
				<div class="d2h-code-wrapper sbs-wrap__left-code">
					<table class="d2h-diff-table">
						<tbody class="d2h-diff-tbody">
						{{{diffs.left}}}
						</tbody>
					</table>
				</div>
			</div>
			<div class="d2h-file-side-diff sbs-wrap__right">
				<div class="d2h-code-wrapper sbs-wrap__right-code">
					<table class="d2h-diff-table">
						<tbody class="d2h-diff-tbody vc__right">
						{{{diffs.right}}}
						</tbody>
					</table>
				</div>
			</div>
		</div>
	</div>`;
	}
}
